import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  appendTokenLog,
  getTokenLog,
  logActivity,
} from '../../services/adminMockData';
import {
  getAllRetailersWithWallet,
  adminAdjustTokens,
  adminUpdateTokens,
  adminSetSubscription,
  getTokenTransactions,
} from '../../services/apiService';

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('nl-NL', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

const REASON_LABELS = {
  purchase: 'Aankoop',
  admin_adjust: 'Aangepast door admin',
  consume: 'Deal gepubliceerd',
  consume_unlimited: 'Deal onder Unlimited (gratis)',
  reset: 'Saldo gereset',
  starter: 'Starttegoed',
};

const n = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

// Token balances per retailer — read from and written to the backend ledger
// (PATCH /admin/shops/:id/tokens). This page used to change only a copy in the
// admin's browser, which is why a change never reached the database or the
// retailer's own screens.
export default function AdminTokensPage() {
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [log, setLog] = useState(() => getTokenLog());
  const [logFromDb, setLogFromDb] = useState(false);
  const [unlimitedUntil, setUnlimitedUntil] = useState('');
  const [modal, setModal] = useState(null);
  const [adjustment, setAdjustment] = useState({ delta: 5, reason: 'Bonus' });
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const refresh = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    // The ledger (token_transactions, backend 0.3); the browser log is only
    // the fallback for an older backend.
    getTokenTransactions({ limit: 20 })
      .then((rows) => {
        setLog(rows.map((t) => ({
          at: t.createdAt, shopName: t.shopName || t.retailerName, delta: Number(t.delta),
          reason: [REASON_LABELS[t.reason] || t.reason, t.note].filter(Boolean).join(' · '),
          admin: t.createdByName || '—',
        })));
        setLogFromDb(true);
      })
      .catch(() => { setLog(getTokenLog()); setLogFromDb(false); });
    try {
      setShops(await getAllRetailersWithWallet());
    } catch (err) {
      setShops([]);
      setLoadError(
        err?.status === 401 ? 'Je admin-sessie is verlopen. Log opnieuw in.'
          : err?.message || 'Kon winkeliers niet laden.'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const overview = useMemo(() => {
    const totalIssued = shops.reduce((s, sh) => s + n(sh.tokensPurchased), 0);
    const totalUsed = shops.reduce((s, sh) => s + n(sh.tokensUsed), 0);
    const unlimited = shops.filter((sh) => sh.subscription === 'unlimited').length;
    return { totalIssued, totalUsed, inCirculation: totalIssued - totalUsed, unlimited };
  }, [shops]);

  const record = (shop, delta, reason) => {
    appendTokenLog({
      shopId: shop.id,
      shopName: shop.shopName,
      delta,
      reason,
      admin: sessionStorage.getItem('walkin_admin_name') || 'Admin',
    });
    setLog(getTokenLog());
  };

  const applyAdjustment = async (shop, delta, reason) => {
    setBusy(true);
    try {
      await adminAdjustTokens(shop, delta, reason);
      record(shop, delta, reason);
      logActivity(delta > 0 ? 'Tokens toegevoegd' : 'Tokens verwijderd', {
        shop: shop.shopName, delta, reason,
      });
      showToast(`${delta > 0 ? '+' : ''}${delta} tokens voor ${shop.shopName} opgeslagen`);
      setModal(null);
      await refresh();
    } catch (err) {
      showToast(err?.message || 'Opslaan mislukt — er is niets gewijzigd', 'error');
    } finally {
      setBusy(false);
    }
  };

  const resetBalance = async (shop) => {
    setBusy(true);
    try {
      // Balance to zero without rewriting history: purchased = used.
      await adminUpdateTokens(shop.id, { purchased: n(shop.tokensUsed), used: n(shop.tokensUsed), reason: 'Saldo gereset' });
      record(shop, -n(shop.tokensAvailable), 'Saldo gereset');
      logActivity('Token saldo gereset', { shop: shop.shopName });
      showToast(`Saldo van ${shop.shopName} op 0 gezet`);
      setModal(null);
      await refresh();
    } catch (err) {
      showToast(err?.message || 'Reset mislukt', 'error');
    } finally {
      setBusy(false);
    }
  };

  // Unlimited is stored in the database (retailer_subscriptions). With an
  // end date the shop falls back to tokens automatically afterwards.
  const setPlan = async (shop, plan) => {
    setBusy(true);
    try {
      await adminSetSubscription(shop.id, {
        plan,
        endsAt: plan === 'unlimited' && unlimitedUntil
          ? new Date(`${unlimitedUntil}T23:59:59`).toISOString() : null,
        source: 'admin',
      });
      logActivity(plan === 'unlimited' ? 'Unlimited toegekend' : 'Unlimited beëindigd', {
        shop: shop.shopName, until: unlimitedUntil || undefined,
      });
      showToast(plan === 'unlimited'
        ? `${shop.shopName} staat op Unlimited${unlimitedUntil ? ` t/m ${unlimitedUntil.split('-').reverse().join('-')}` : ''}`
        : `${shop.shopName} staat weer op tokens`);
      setModal(null);
      setUnlimitedUntil('');
      await refresh();
    } catch (err) {
      showToast(
        err?.status === 404
          ? 'De backend heeft de Unlimited-uitbreiding (0.3) nog niet'
          : err?.message || 'Opslaan mislukt',
        'error'
      );
    } finally {
      setBusy(false);
    }
  };

  const bulkGiveAll = async (delta) => {
    const targets = shops.filter((s) => s.status === 'actief' && s.subscription !== 'unlimited');
    if (targets.length === 0) return;
    setBusy(true);
    const reason = `Bulk bonus +${delta} aan alle actieve winkeliers`;
    const results = await Promise.allSettled(targets.map((s) => adminAdjustTokens(s, delta)));
    results.forEach((r, i) => { if (r.status === 'fulfilled') record(targets[i], delta, reason); });
    const failed = results.filter((r) => r.status === 'rejected').length;
    logActivity('Bulk token actie', { delta, count: targets.length - failed });
    showToast(
      failed ? `${failed} van ${targets.length} winkeliers niet bijgewerkt` : `+${delta} tokens voor ${targets.length} winkeliers`,
      failed ? 'error' : 'success'
    );
    setBusy(false);
    await refresh();
  };

  return (
    <div>
      {toast && (
        <div className={`admin__toast admin__toast--${toast.type}`}>{toast.message}</div>
      )}

      <header className="admin-page-head">
        <div>
          <h1 className="admin-page-title">Tokens</h1>
          <p className="admin-page-sub">
            Beheer token-saldi per winkelier — wijzigingen worden direct in de database opgeslagen
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="admin-btn admin-btn--ghost" onClick={() => bulkGiveAll(5)} disabled={busy || loading}>
            🎁 +5 voor alle actieve
          </button>
        </div>
      </header>

      {loadError && (
        <div className="admin-card" style={{ color: '#991B1B' }}>{loadError}</div>
      )}

      <div className="admin-grid" style={{ marginBottom: 18 }}>
        <div className="admin-stat">
          <span className="admin-stat__label">Tokens uitgegeven</span>
          <span className="admin-stat__value">{overview.totalIssued}</span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat__label">Tokens gebruikt</span>
          <span className="admin-stat__value">{overview.totalUsed}</span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat__label">Tokens in omloop</span>
          <span className="admin-stat__value">{overview.inCirculation}</span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat__label">Unlimited</span>
          <span className="admin-stat__value">{overview.unlimited}</span>
        </div>
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Winkelnaam</th>
              <th>Status</th>
              <th>Plan</th>
              <th>Toegekend</th>
              <th>Gebruikt</th>
              <th>Beschikbaar</th>
              <th>Lid sinds</th>
              <th>Acties</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="admin-empty">Laden…</td></tr>
            )}
            {!loading && shops.length === 0 && (
              <tr><td colSpan={8} className="admin-empty">Geen winkeliers gevonden</td></tr>
            )}
            {!loading && shops.map((s) => (
              <tr key={s.id}>
                <td>{s.shopName}</td>
                <td>
                  <span className={`admin-badge admin-badge--${s.status || 'actief'}`}>{s.status || 'actief'}</span>
                </td>
                <td>
                  {s.subscription === 'unlimited' ? (
                    <span className="admin-badge admin-badge--unlimited">
                      ∞ Unlimited{s.subscription_end_date ? ` t/m ${new Date(s.subscription_end_date).toLocaleDateString('nl-NL')}` : ''}
                    </span>
                  ) : (
                    <span className="admin-badge admin-badge--starter">Tokens</span>
                  )}
                </td>
                <td>{s.tokensPurchased ?? '—'}</td>
                <td>{s.tokensUsed ?? '—'}</td>
                <td><strong>{s.tokensAvailable ?? '—'}</strong></td>
                <td>{s.createdAt ? new Date(s.createdAt).toLocaleDateString('nl-NL') : '—'}</td>
                <td>
                  <button
                    className="admin-btn admin-btn--small admin-btn--ghost"
                    onClick={() => setModal(s)}
                    disabled={s.tokensPurchased === undefined}
                    title={s.tokensPurchased === undefined ? 'Saldo kon niet worden geladen' : undefined}
                  >
                    Aanpassen
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="admin-card">
        <div className="admin-card__title">Token mutaties (laatste 20{logFromDb ? '' : ', vanuit deze browser'})</div>
        {log.length === 0 ? (
          <div className="admin-empty">Nog geen mutaties gelogd.</div>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr><th>Datum</th><th>Winkel</th><th>Mutatie</th><th>Reden</th><th>Door</th></tr>
              </thead>
              <tbody>
                {log.slice(0, 20).map((entry, idx) => (
                  <tr key={idx}>
                    <td>{formatDate(entry.at)}</td>
                    <td>{entry.shopName}</td>
                    <td style={{ fontWeight: 700, color: entry.delta > 0 ? '#16A34A' : entry.delta < 0 ? '#DC2626' : '#6B7280' }}>
                      {entry.delta > 0 ? '+' : ''}{entry.delta === 0 ? '∞' : entry.delta}
                    </td>
                    <td>{entry.reason}</td>
                    <td>{entry.admin}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <div className="admin-modal" role="dialog" onClick={() => !busy && setModal(null)}>
          <div className="admin-modal__card" onClick={(e) => e.stopPropagation()}>
            <h3 className="admin-modal__title">Tokens aanpassen — {modal.shopName}</h3>
            <p className="admin-modal__text">
              Huidig saldo: <strong>{n(modal.tokensAvailable)}</strong> beschikbaar
              {' '}({n(modal.tokensUsed)} gebruikt van {n(modal.tokensPurchased)} toegekend)
            </p>
            <div className="admin-form-row">
              <div className="admin-field">
                <span className="admin-field__label">Mutatie (+/-)</span>
                <input
                  className="admin-input"
                  type="number"
                  value={adjustment.delta}
                  onChange={(e) => setAdjustment({ ...adjustment, delta: parseInt(e.target.value || '0', 10) })}
                />
                <span className="admin-field__hint">
                  Nieuw saldo: {Math.max(0, n(modal.tokensAvailable) + n(adjustment.delta))}
                </span>
              </div>
              <div className="admin-field">
                <span className="admin-field__label">Reden</span>
                <input
                  className="admin-input"
                  value={adjustment.reason}
                  onChange={(e) => setAdjustment({ ...adjustment, reason: e.target.value })}
                />
              </div>
            </div>
            <div className="admin-field" style={{ marginTop: 8 }}>
              <span className="admin-field__label">
                {modal.subscription === 'unlimited' ? 'Unlimited' : 'Unlimited toekennen'}
              </span>
              {modal.subscription === 'unlimited' ? (
                <div style={{ fontSize: 13 }}>
                  Actief{modal.subscription_end_date
                    ? ` t/m ${new Date(modal.subscription_end_date).toLocaleDateString('nl-NL')}` : ' (doorlopend)'}
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    className="admin-input"
                    type="date"
                    style={{ maxWidth: 180 }}
                    value={unlimitedUntil}
                    min={new Date().toISOString().slice(0, 10)}
                    onChange={(e) => setUnlimitedUntil(e.target.value)}
                  />
                  <span className="admin-field__hint">t/m (optioneel; leeg = doorlopend)</span>
                </div>
              )}
            </div>
            <div className="admin-modal__actions" style={{ flexWrap: 'wrap', justifyContent: 'flex-start' }}>
              <button
                className="admin-btn"
                disabled={busy || !n(adjustment.delta)}
                onClick={() => applyAdjustment(modal, n(adjustment.delta), adjustment.reason)}
              >
                {busy ? 'Opslaan…' : 'Toepassen'}
              </button>
              <button
                className="admin-btn admin-btn--ghost"
                disabled={busy}
                onClick={() => resetBalance(modal)}
              >
                Reset saldo
              </button>
              {modal.subscription === 'unlimited' ? (
                <button className="admin-btn admin-btn--ghost" disabled={busy}
                  onClick={() => setPlan(modal, 'tokens')}>
                  Terug naar tokens
                </button>
              ) : (
                <button className="admin-btn admin-btn--ghost" disabled={busy}
                  onClick={() => setPlan(modal, 'unlimited')}>
                  ∞ Naar Unlimited
                </button>
              )}
              <button
                className="admin-btn admin-btn--ghost"
                disabled={busy}
                onClick={() => setModal(null)}
                style={{ marginLeft: 'auto' }}
              >
                Annuleren
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
